import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import type { StripeAccountStatus } from '../../types/nurseryCounter';

/**
 * The nursery's own Stripe account, for taking cards at the counter.
 *
 * The account is the nursery's: it is opened in Stripe's own onboarding, the
 * money goes straight into it, Stripe's fees and payouts are between the
 * nursery and Stripe, and the nursery has its own Stripe dashboard. We keep
 * only which account it is and whether it can take payments yet. Website
 * orders are not affected.
 */

function CardPayments() {
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<StripeAccountStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'connect' | 'disconnect' | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getStripeAccount();
      if (res.success) setStatus(res.data);
      else setError(errorMessage(res, 'The Stripe account could not be read'));
    } catch (e) {
      setError(errorMessage(e, 'The Stripe account could not be read'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe: the account was just read again, so the flag can go.
  const returned = params.get('return') === '1';
  const refresh = params.get('refresh') === '1';
  useEffect(() => {
    if (!returned && !refresh) return;
    const next = new URLSearchParams(params);
    next.delete('return');
    next.delete('refresh');
    setParams(next, { replace: true });
  }, [returned, refresh, params, setParams]);

  const connect = async () => {
    setBusy('connect');
    setError(null);
    try {
      const res = await nurseryCounterService.startStripeOnboarding();
      if (res.success && res.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      setError(errorMessage(res, 'Stripe could not be opened'));
    } catch (e) {
      setError(errorMessage(e, 'Stripe could not be opened'));
    }
    setBusy(null);
  };

  const disconnect = async () => {
    setBusy('disconnect');
    setError(null);
    try {
      const res = await nurseryCounterService.disconnectStripe();
      if (res.success) {
        setStatus(res.data);
        setConfirmDisconnect(false);
      } else setError(errorMessage(res, 'The account could not be disconnected'));
    } catch (e) {
      setError(errorMessage(e, 'The account could not be disconnected'));
    }
    setBusy(null);
  };

  return (
    <>
      <PageHeader
        title="Card payments"
        subtitle="Take cards at the counter into your own Stripe account"
        icon={<CreditCard className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <h2 className="text-base font-semibold text-neutral-900">How it works</h2>
          <ul className="text-sm text-neutral-700 space-y-1 list-disc pl-5 break-words">
            <li>On a walk-in sale's order, Card shows a QR code. The customer scans it and pays on their own phone.</li>
            <li>The money goes straight into your Stripe account. Stripe's fees and payouts are between you and Stripe; FactreeTech takes nothing.</li>
            <li>Refunds for trees not supplied go back to the card from your account.</li>
            <li>When the invoice goes to Xero, the payment is recorded against it in the card clearing account you choose on the Xero page.</li>
          </ul>
        </section>

        {status && !status.stripe_configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            Card payments are not set up on this server yet.
          </div>
        )}

        {status && status.stripe_configured && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Your Stripe account">
            <h2 className="text-base font-semibold text-neutral-900">Your Stripe account</h2>
            {!status.connected ? (
              <p className="text-sm text-neutral-700 break-words">
                Not connected. Connecting opens Stripe, where you set up a Stripe account for the business (or sign in to one)
                and give Stripe the details it asks for.
              </p>
            ) : status.ready ? (
              <p className="flex items-start gap-2 text-sm text-green-900 break-words">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {status.business_name ? `${status.business_name}: ` : ''}cards can be taken at the counter.
                  {!status.payouts_enabled && ' Stripe has not yet switched on payouts to your bank; it says what it still needs in your Stripe dashboard.'}
                </span>
              </p>
            ) : (
              <p className="flex items-start gap-2 text-sm text-amber-900 break-words">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {status.details_submitted
                    ? 'Stripe is checking the details given. Cards can be taken once it has finished.'
                    : 'Stripe still needs some details before cards can be taken. Carry on where you left off.'}
                </span>
              </p>
            )}
            {status.connected && status.stripe_account_id && (
              <p className="text-xs text-neutral-500 break-all">Stripe account {status.stripe_account_id}</p>
            )}

            {isAdmin ? (
              <div className="flex flex-wrap gap-2">
                {(!status.connected || !status.ready) && (
                  <Button onClick={connect} disabled={busy !== null}>
                    <ExternalLink className="h-4 w-4" />
                    {busy === 'connect' ? 'Opening Stripe…' : status.connected ? 'Carry on in Stripe' : 'Connect Stripe'}
                  </Button>
                )}
                {status.connected && !confirmDisconnect && (
                  <Button variant="outline" onClick={() => setConfirmDisconnect(true)} disabled={busy !== null}>Disconnect</Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 break-words">An Accounts Admin connects and disconnects the Stripe account.</p>
            )}

            {confirmDisconnect && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 text-sm text-amber-900 space-y-2 break-words">
                <p>
                  Cards can no longer be taken at the counter until an account is connected again. The Stripe account stays
                  yours, with its money and history, and refunds of payments already taken still go back through it.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="danger" size="sm" onClick={disconnect} disabled={busy !== null}>
                    {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(false)} disabled={busy !== null}>Keep it</Button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}

export default CardPayments;
